/**
 * Smart Merge — per-entity conflict detection and 3-way merge for manuscript payloads.
 *
 * Instead of treating the entire ManuscriptBackupPayload as one monolithic blob,
 * this module computes individual hashes for each entity (manuscript metadata,
 * planner structure, each scene, each codex entry, goals, format settings) and
 * merges non-overlapping changes automatically.
 *
 * Only truly overlapping edits (e.g. both devices edited the same scene) trigger
 * a conflict copy.
 */

import type { ManuscriptBackupPayload } from '@/lib/backupPayload';
import type { StructureItem } from '@/types/database';
import { getSha256, stringifyCanonicalJson } from '@/utils/hash';

/** Per-entity hash map stored at sync time */
export interface EntityHashes {
  manuscript?: string;
  plannerStructure?: string;
  scenes: Record<string, string>;   // sceneId → hash
  codex: Record<string, string>;    // codexEntryId → hash
  goals?: string;
  formatSettings?: string;
}

export interface SmartMergeResult {
  merged: ManuscriptBackupPayload;
  hasConflicts: boolean;
  /** Scene IDs where both local and cloud made changes */
  conflictingSceneIds: string[];
  /** Codex entry IDs where both local and cloud made changes */
  conflictingCodexIds: string[];
  /** Whether the planner structure itself conflicted */
  plannerConflict: boolean;
  /** Whether manuscript metadata conflicted */
  manuscriptMetadataConflict: boolean;
  /** Scene IDs that had content but were missing from the merged planner structure (auto-restored) */
  orphanedSceneIds: string[];
}

/** Recursively collect all scene IDs referenced in a planner structure tree. */
export function collectSceneIdsFromStructure(items: StructureItem[]): Set<string> {
  const ids = new Set<string>();
  for (const item of items) {
    if (item.type === 'scene') {
      ids.add(item.id);
    }
    if (item.children) {
      for (const childId of collectSceneIdsFromStructure(item.children)) {
        ids.add(childId);
      }
    }
  }
  return ids;
}

/**
 * Compute per-entity hashes for a ManuscriptBackupPayload.
 * These are stored in localStorage at sync time and used for 3-way merge detection.
 */
export async function computeEntityHashes(payload: ManuscriptBackupPayload): Promise<EntityHashes> {
  const result: EntityHashes = { scenes: {}, codex: {} };

  if (payload.manuscript) {
    // Hash manuscript metadata WITHOUT lastModified (which changes constantly)
    const { lastModified, wordCount, ...stableMetadata } = payload.manuscript;
    result.manuscript = await getSha256(stringifyCanonicalJson(stableMetadata));
  }

  if (payload.plannerStructure) {
    result.plannerStructure = await getSha256(stringifyCanonicalJson(payload.plannerStructure));
  }

  if (payload.sceneContent) {
    for (const scene of payload.sceneContent) {
      result.scenes[scene.id] = await getSha256(stringifyCanonicalJson({ id: scene.id, content: scene.content }));
    }
  }

  if (payload.codexEntries) {
    for (const entry of payload.codexEntries) {
      result.codex[entry.id] = await getSha256(stringifyCanonicalJson(entry));
    }
  }

  if (payload.goals) {
    result.goals = await getSha256(stringifyCanonicalJson(payload.goals));
  }

  if (payload.formatSettings) {
    result.formatSettings = await getSha256(stringifyCanonicalJson(payload.formatSettings));
  }

  return result;
}

/**
 * Perform a 3-way smart merge between a local payload, a cloud payload,
 * and the per-entity hashes that were stored at the last successful sync.
 *
 * For each entity:
 *   - local hash == synced hash && cloud differs → cloud changed only → use cloud
 *   - local hash != synced hash && cloud same    → local changed only → use local
 *   - both differ from synced                    → true conflict → use cloud, flag it
 *   - both same as synced                        → no change → either is fine (use cloud for consistency)
 */
export async function smartMergePayloads(
  local: ManuscriptBackupPayload,
  cloud: ManuscriptBackupPayload,
  syncedHashes: EntityHashes
): Promise<SmartMergeResult> {
  const localHashes = await computeEntityHashes(local);
  const cloudHashes = await computeEntityHashes(cloud);

  const conflictingSceneIds: string[] = [];
  const conflictingCodexIds: string[] = [];
  let plannerConflict = false;
  let manuscriptMetadataConflict = false;

  let mergedManuscript = cloud.manuscript;
  let mergedPlanner = cloud.plannerStructure;
  let mergedGoals = cloud.goals;
  let mergedFormat = cloud.formatSettings;

  // ── Manuscript metadata ──
  if (localHashes.manuscript === cloudHashes.manuscript) {
    // Both sides are identical — no conflict
    mergedManuscript = cloud.manuscript;
  } else if (!syncedHashes.manuscript) {
    // No baseline: prefer the version with more recent lastModified, or cloud
    const localTime = local.manuscript?.lastModified ? new Date(local.manuscript.lastModified).getTime() : 0;
    const cloudTime = cloud.manuscript?.lastModified ? new Date(cloud.manuscript.lastModified).getTime() : 0;
    mergedManuscript = localTime > cloudTime ? local.manuscript : cloud.manuscript;
  } else {
    const localMetaChanged = localHashes.manuscript !== syncedHashes.manuscript;
    const cloudMetaChanged = cloudHashes.manuscript !== syncedHashes.manuscript;
    if (localMetaChanged && !cloudMetaChanged) {
      mergedManuscript = local.manuscript;
    } else if (localMetaChanged && cloudMetaChanged) {
      manuscriptMetadataConflict = true;
      mergedManuscript = cloud.manuscript; // cloud wins for true conflicts
    }
  }

  // ── Planner structure (treated as single entity) ──
  if (localHashes.plannerStructure === cloudHashes.plannerStructure) {
    // Both sides are identical — no conflict
    mergedPlanner = cloud.plannerStructure;
  } else if (!syncedHashes.plannerStructure) {
    // No baseline: prefer structure with more items, else cloud
    const localCount = local.plannerStructure?.structure?.length || 0;
    const cloudCount = cloud.plannerStructure?.structure?.length || 0;
    if (localCount > 0 && cloudCount === 0) {
      mergedPlanner = local.plannerStructure;
    } else {
      mergedPlanner = cloud.plannerStructure;
      if (localCount > 0 && cloudCount > 0) {
        plannerConflict = true;
      }
    }
  } else {
    const localPlannerChanged = localHashes.plannerStructure !== syncedHashes.plannerStructure;
    const cloudPlannerChanged = cloudHashes.plannerStructure !== syncedHashes.plannerStructure;
    if (localPlannerChanged && !cloudPlannerChanged) {
      mergedPlanner = local.plannerStructure;
    } else if (localPlannerChanged && cloudPlannerChanged) {
      plannerConflict = true;
      mergedPlanner = cloud.plannerStructure; // cloud wins
    }
  }

  // ── Scene content (per-scene merge) ──
  // Build a map of all scene IDs across local, cloud, and synced
  const allSceneIds = new Set<string>([
    ...Object.keys(syncedHashes.scenes),
    ...Object.keys(localHashes.scenes),
    ...Object.keys(cloudHashes.scenes),
  ]);

  const localSceneMap = new Map(
    (local.sceneContent || []).map(s => [s.id, s])
  );
  const cloudSceneMap = new Map(
    (cloud.sceneContent || []).map(s => [s.id, s])
  );

  const mergedScenes: ManuscriptBackupPayload['sceneContent'] = [];

  for (const sceneId of allSceneIds) {
    const syncedHash = syncedHashes.scenes[sceneId];
    const localHash = localHashes.scenes[sceneId];
    const cloudHash = cloudHashes.scenes[sceneId];

    // Fast-path: local and cloud have identical content
    if (localHash && cloudHash && localHash === cloudHash) {
      mergedScenes.push(cloudSceneMap.get(sceneId) || localSceneMap.get(sceneId)!);
      continue;
    }

    // If no baseline was recorded for this scene:
    if (!syncedHash) {
      if (localSceneMap.has(sceneId) && !cloudSceneMap.has(sceneId)) {
        // Scene only exists locally
        mergedScenes.push(localSceneMap.get(sceneId)!);
      } else if (!localSceneMap.has(sceneId) && cloudSceneMap.has(sceneId)) {
        // Scene only exists in cloud
        mergedScenes.push(cloudSceneMap.get(sceneId)!);
      } else if (localSceneMap.has(sceneId) && cloudSceneMap.has(sceneId)) {
        // Both exist but hashes differ and no baseline: check if one is empty
        const localContent = localSceneMap.get(sceneId)?.content?.replace(/<[^>]*>/g, '').trim() || '';
        const cloudContent = cloudSceneMap.get(sceneId)?.content?.replace(/<[^>]*>/g, '').trim() || '';
        if (localContent && !cloudContent) {
          mergedScenes.push(localSceneMap.get(sceneId)!);
        } else if (!localContent && cloudContent) {
          mergedScenes.push(cloudSceneMap.get(sceneId)!);
        } else {
          // Both have differing non-empty content without baseline: true conflict
          conflictingSceneIds.push(sceneId);
          mergedScenes.push(cloudSceneMap.get(sceneId)!);
        }
      }
      continue;
    }

    const localChanged = localHash !== syncedHash;
    const cloudChanged = cloudHash !== syncedHash;

    if (localChanged && !cloudChanged && localSceneMap.has(sceneId)) {
      // Only local changed — keep local
      mergedScenes.push(localSceneMap.get(sceneId)!);
    } else if (!localChanged && cloudChanged && cloudSceneMap.has(sceneId)) {
      // Only cloud changed — keep cloud
      mergedScenes.push(cloudSceneMap.get(sceneId)!);
    } else if (localChanged && cloudChanged) {
      // Both changed to different hashes — true conflict, cloud wins
      conflictingSceneIds.push(sceneId);
      if (cloudSceneMap.has(sceneId)) {
        mergedScenes.push(cloudSceneMap.get(sceneId)!);
      } else if (localSceneMap.has(sceneId)) {
        mergedScenes.push(localSceneMap.get(sceneId)!);
      }
    } else {
      // Neither changed, or new scene — use cloud if available, else local
      if (cloudSceneMap.has(sceneId)) {
        mergedScenes.push(cloudSceneMap.get(sceneId)!);
      } else if (localSceneMap.has(sceneId)) {
        mergedScenes.push(localSceneMap.get(sceneId)!);
      }
    }
  }

  // ── Codex entries (per-entry merge) ──
  const allCodexIds = new Set<string>([
    ...Object.keys(syncedHashes.codex),
    ...Object.keys(localHashes.codex),
    ...Object.keys(cloudHashes.codex),
  ]);

  const localCodexMap = new Map(
    (local.codexEntries || []).map(e => [e.id, e])
  );
  const cloudCodexMap = new Map(
    (cloud.codexEntries || []).map(e => [e.id, e])
  );

  const mergedCodex: ManuscriptBackupPayload['codexEntries'] = [];

  for (const codexId of allCodexIds) {
    const syncedHash = syncedHashes.codex[codexId];
    const localHash = localHashes.codex[codexId];
    const cloudHash = cloudHashes.codex[codexId];

    // Fast-path: local and cloud have identical content
    if (localHash && cloudHash && localHash === cloudHash) {
      mergedCodex.push(cloudCodexMap.get(codexId) || localCodexMap.get(codexId)!);
      continue;
    }

    // If no baseline was recorded for this codex entry:
    if (!syncedHash) {
      if (localCodexMap.has(codexId) && !cloudCodexMap.has(codexId)) {
        mergedCodex.push(localCodexMap.get(codexId)!);
      } else if (!localCodexMap.has(codexId) && cloudCodexMap.has(codexId)) {
        mergedCodex.push(cloudCodexMap.get(codexId)!);
      } else if (localCodexMap.has(codexId) && cloudCodexMap.has(codexId)) {
        conflictingCodexIds.push(codexId);
        mergedCodex.push(cloudCodexMap.get(codexId)!);
      }
      continue;
    }

    const localChanged = localHash !== syncedHash;
    const cloudChanged = cloudHash !== syncedHash;

    if (localChanged && !cloudChanged && localCodexMap.has(codexId)) {
      mergedCodex.push(localCodexMap.get(codexId)!);
    } else if (!localChanged && cloudChanged && cloudCodexMap.has(codexId)) {
      mergedCodex.push(cloudCodexMap.get(codexId)!);
    } else if (localChanged && cloudChanged) {
      conflictingCodexIds.push(codexId);
      if (cloudCodexMap.has(codexId)) {
        mergedCodex.push(cloudCodexMap.get(codexId)!);
      } else if (localCodexMap.has(codexId)) {
        mergedCodex.push(localCodexMap.get(codexId)!);
      }
    } else {
      if (cloudCodexMap.has(codexId)) {
        mergedCodex.push(cloudCodexMap.get(codexId)!);
      } else if (localCodexMap.has(codexId)) {
        mergedCodex.push(localCodexMap.get(codexId)!);
      }
    }
  }

  // ── Goals ──
  if (localHashes.goals === cloudHashes.goals) {
    mergedGoals = cloud.goals;
  } else if (!syncedHashes.goals) {
    mergedGoals = cloud.goals || local.goals;
  } else {
    const localGoalsChanged = localHashes.goals !== syncedHashes.goals;
    const cloudGoalsChanged = cloudHashes.goals !== syncedHashes.goals;
    if (localGoalsChanged && !cloudGoalsChanged) {
      mergedGoals = local.goals;
    } else {
      mergedGoals = cloud.goals;
    }
  }

  // ── Format settings ──
  if (localHashes.formatSettings === cloudHashes.formatSettings) {
    mergedFormat = cloud.formatSettings;
  } else if (!syncedHashes.formatSettings) {
    mergedFormat = cloud.formatSettings || local.formatSettings;
  } else {
    const localFormatChanged = localHashes.formatSettings !== syncedHashes.formatSettings;
    const cloudFormatChanged = cloudHashes.formatSettings !== syncedHashes.formatSettings;
    if (localFormatChanged && !cloudFormatChanged) {
      mergedFormat = local.formatSettings;
    } else {
      mergedFormat = cloud.formatSettings;
    }
  }

  // ── Orphan Scene Guard ──
  // Check if any scene in mergedScenes is missing from the merged planner
  // structure. This happens when one device deletes a chapter (removing scenes
  // from the outline) while another device edits prose in those scenes.
  // Auto-append a "Restored Scenes" chapter so no prose content is lost.
  const orphanedSceneIds: string[] = [];
  if (mergedPlanner?.structure) {
    const structureSceneIds = collectSceneIdsFromStructure(mergedPlanner.structure);
    for (const scene of mergedScenes) {
      if (!structureSceneIds.has(scene.id)) {
        orphanedSceneIds.push(scene.id);
      }
    }

    if (orphanedSceneIds.length > 0) {
      console.warn(
        `[SmartMerge] Orphan scene guard: ${orphanedSceneIds.length} scene(s) have content but are missing from planner structure. Appending "Restored Scenes" chapter.`,
        orphanedSceneIds
      );

      const restoredChapter: StructureItem = {
        id: 'restored-scenes',
        type: 'chapter',
        title: 'Restored Scenes',
        children: orphanedSceneIds.map(sceneId => ({
          id: sceneId,
          type: 'scene' as const,
          title: 'Restored Scene',
        })),
      };

      const structure = mergedPlanner.structure.map(item => ({ ...item }));
      let appended = false;
      for (let i = structure.length - 1; i >= 0; i--) {
        if (structure[i].type === 'act') {
          structure[i] = {
            ...structure[i],
            children: [...(structure[i].children || []), restoredChapter],
          };
          appended = true;
          break;
        }
      }
      if (!appended) {
        structure.push(restoredChapter);
      }

      mergedPlanner = { ...mergedPlanner, structure };
    }
  }

  // ── Assemble merged payload ──
  const hasConflicts = conflictingSceneIds.length > 0 ||
    conflictingCodexIds.length > 0 ||
    plannerConflict ||
    manuscriptMetadataConflict;

  const merged: ManuscriptBackupPayload = {
    manuscript: mergedManuscript,
    plannerStructure: mergedPlanner,
    sceneContent: mergedScenes,
    codexEntries: mergedCodex,
    goals: mergedGoals,
    formatSettings: mergedFormat,
    series: cloud.series || local.series,
    collection: cloud.collection || local.collection,
  };

  return {
    merged,
    hasConflicts,
    conflictingSceneIds,
    conflictingCodexIds,
    plannerConflict,
    manuscriptMetadataConflict,
    orphanedSceneIds,
  };
}
